import { alpha, createTheme, darken, getContrastRatio, lighten } from '@mui/material/styles';

/**
 * Dark: "Obsidian Architect" — amber on near-black (see style.md).
 * Light: "Ethereal Professional" — electric blue on cloud gray, tonal nesting (no hard lines).
 */
const OBSIDIAN_DARK = {
  background: '#0e0e0e',
  backgroundLowest: '#000000',
  surfaceLow: '#131313',
  paper: '#191919',
  surfaceHigh: '#1f1f1f',
  surfaceHighest: '#262626',
  surfaceVariant: '#262626',
  nav: '#0e0e0e',
  onSurface: '#ffffff',
  onSurfaceVariant: '#ababab',
  outlineVariant: '#484848',
  outline: '#757575',
  primary: '#f8a826',
  primaryDark: '#df9305',
  primaryLight: '#ffca5d',
  onPrimary: '#4e3000',
  onPrimaryContainer: '#362100',
  secondary: '#ffbf2a',
  onSecondary: '#573e00',
  error: '#ff7351',
  errorOn: '#450900',
  errorContainer: '#b92902',
} as const;

const ETHEREAL_LIGHT = {
  surface: '#f6f6f7',
  surfaceContainerLow: '#f0f1f2',
  surfaceContainer: '#e7e8e9',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerHigh: '#ecedee',
  primary: '#0057bf',
  primaryDark: '#004494',
  primaryLight: '#6f9fff',
  onPrimary: '#ffffff',
  onSurface: '#2d2f30',
  onSurfaceVariant: '#5c5e63',
  outlineVariant: '#acadae',
  outline: '#8e9092',
  secondaryMuted: '#5c5e63',
  error: '#b3261e',
  errorOn: '#ffffff',
  errorContainer: '#f9dedc',
} as const;

/** Brand default primary (for color picker preview when user has no custom color). */
export const GLUBES_BRAND_PRIMARY_HEX: Record<'light' | 'dark', string> = {
  light: ETHEREAL_LIGHT.primary,
  dark: OBSIDIAN_DARK.primary,
};

const fontStack =
  '-apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif';

const typographyDark = {
  fontFamily: fontStack,
  h1: {
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.2,
  },
  h2: { fontWeight: 800, letterSpacing: '-0.02em' },
  h3: { fontWeight: 700 },
  button: { fontWeight: 700, letterSpacing: '0.02em' },
} as const;

const typographyLight = {
  fontFamily: fontStack,
  h1: {
    fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  h2: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.35 },
  h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
  h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
  body1: { fontSize: '0.875rem', lineHeight: 1.6 },
  body2: { fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 500 },
  button: { fontSize: '0.875rem', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'none' as const },
  caption: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.5,
  },
  subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
} as const;

const ambientFloat = '0px 10px 40px rgba(45, 47, 48, 0.06)';

export type CreateGlubesMuiThemeOptions = {
  /** Overrides brand primary in light mode. */
  primaryMainLight?: string | null;
  /** Overrides brand primary in dark mode. */
  primaryMainDark?: string | null;
};

type PrimaryShades = {
  main: string;
  dark: string;
  light: string;
  contrastText: string;
};

function buildPrimaryFromMain(main: string): PrimaryShades {
  const contrastText = getContrastRatio('#ffffff', main) >= 4.5 ? '#ffffff' : '#000000';
  return {
    main,
    dark: darken(main, 0.18),
    light: lighten(main, 0.18),
    contrastText,
  };
}

export function createGlubesMuiTheme(
  resolvedAppearance: 'light' | 'dark',
  options?: CreateGlubesMuiThemeOptions
) {
  const mode = resolvedAppearance === 'dark' ? 'dark' : 'light';
  const isLight = mode === 'light';

  if (isLight) {
    const E = ETHEREAL_LIGHT;
    const P: PrimaryShades =
      options?.primaryMainLight != null && options.primaryMainLight !== ''
        ? buildPrimaryFromMain(options.primaryMainLight)
        : { main: E.primary, dark: E.primaryDark, light: E.primaryLight, contrastText: E.onPrimary };
    return createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: P.main,
          dark: P.dark,
          light: P.light,
          contrastText: P.contrastText,
        },
        secondary: {
          main: E.secondaryMuted,
          contrastText: E.surfaceContainerLowest,
        },
        error: {
          main: E.error,
          contrastText: E.errorOn,
        },
        background: {
          default: E.surface,
          paper: E.surfaceContainerLowest,
          surfaceLowest: E.surfaceContainerLowest,
          surfaceLow: E.surfaceContainerLow,
          surfaceHigh: E.surfaceContainer,
          surfaceHighest: E.surfaceContainerHigh,
          nav: E.surfaceContainerLow,
        },
        text: {
          primary: E.onSurface,
          secondary: E.onSurfaceVariant,
          disabled: E.outline,
        },
        divider: 'rgba(45, 47, 48, 0.06)',
        action: {
          hover: 'rgba(45, 47, 48, 0.04)',
          selected: alpha(P.main, 0.08),
        },
      },
      typography: typographyLight,
      shape: { borderRadius: 12 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              fontFamily: fontStack,
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            },
            '.material-symbols-outlined': {
              fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
              verticalAlign: 'middle',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 12,
            },
            containedPrimary: ({ theme }) => ({
              color: P.contrastText,
              backgroundColor: 'transparent',
              backgroundImage: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: 'transparent',
                backgroundImage: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
                filter: 'brightness(0.97)',
              },
              '&.Mui-disabled': {
                backgroundImage: 'none',
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }),
            containedSecondary: {
              color: P.main,
              backgroundColor: E.surfaceContainerHigh,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: E.surfaceContainer,
              },
            },
            textPrimary: {
              color: P.main,
              '&:hover': { backgroundColor: alpha(P.main, 0.06) },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
            elevation1: { boxShadow: ambientFloat },
            elevation2: { boxShadow: ambientFloat },
            elevation3: { boxShadow: ambientFloat },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              boxShadow: 'none',
              transition: 'box-shadow 0.2s ease',
              '&:hover': {
                boxShadow: ambientFloat,
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundColor: E.surfaceContainerLow,
              transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused': {
                backgroundColor: E.surfaceContainerLowest,
                boxShadow: `0 0 0 2px ${alpha(P.main, 0.2)}`,
              },
              '&.Mui-error.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(179, 38, 30, 0.25)',
              },
            },
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundColor: E.surfaceContainerLow,
              '&::before, &::after': { display: 'none' },
              '&:hover': { backgroundColor: E.surfaceContainerLow },
              '&.Mui-focused': {
                backgroundColor: E.surfaceContainerLowest,
                boxShadow: `0 0 0 2px ${alpha(P.main, 0.2)}`,
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: E.onSurfaceVariant,
              fontSize: '0.75rem',
              fontWeight: 500,
              '&.Mui-focused': { color: E.onSurfaceVariant },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              backgroundColor: E.surfaceContainerHigh,
              fontSize: '0.75rem',
              fontWeight: 500,
            },
            colorPrimary: {
              color: P.contrastText,
              backgroundColor: P.main,
              '&:hover': { backgroundColor: P.dark },
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: 'rgba(45, 47, 48, 0.06)',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: 16,
              boxShadow: ambientFloat,
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              borderRadius: 12,
              boxShadow: ambientFloat,
            },
          },
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              borderRadius: 12,
              boxShadow: ambientFloat,
            },
          },
        },
      },
    });
  }

  const O = OBSIDIAN_DARK;
  const P: PrimaryShades =
    options?.primaryMainDark != null && options.primaryMainDark !== ''
      ? buildPrimaryFromMain(options.primaryMainDark)
      : { main: O.primary, dark: O.primaryDark, light: O.primaryLight, contrastText: O.onPrimary };
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: P.main,
        dark: P.dark,
        light: P.light,
        contrastText: P.contrastText,
      },
      secondary: {
        main: O.secondary,
        contrastText: O.onSecondary,
      },
      error: {
        main: O.error,
        contrastText: O.errorOn,
      },
      background: {
        default: O.background,
        paper: O.paper,
        surfaceLowest: O.backgroundLowest,
        surfaceLow: O.surfaceLow,
        surfaceHigh: O.surfaceHigh,
        surfaceHighest: O.surfaceHighest,
        nav: O.nav,
      },
      text: {
        primary: O.onSurface,
        secondary: O.onSurfaceVariant,
        disabled: O.outline,
      },
      divider: 'rgba(255,255,255,0.06)',
      action: {
        hover: 'rgba(255,255,255,0.05)',
        selected: alpha(P.main, 0.08),
      },
    },
    typography: typographyDark,
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: fontStack,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '.material-symbols-outlined': {
            fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
            verticalAlign: 'middle',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
}

/** Layout chrome tokens (header shadow, ghost outlines, nav affordances). */
export function glubesChrome(theme: import('@mui/material/styles').Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    headerShadow: isDark ? '0 20px 40px rgba(0,0,0,0.4)' : '0 8px 32px rgba(45,47,48,0.04)',
    navActiveBorder: isDark ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
    outlineSubtle: isDark ? 'rgba(72,72,72,0.35)' : 'rgba(172, 173, 174, 0.15)',
    ambientFloat,
  };
}

/** @deprecated Use {@link glubesChrome} */
export const obsidianChrome = glubesChrome;
